export type StringValidator = {
  predicate: (value: string) => boolean;
  message: string;
};

const stringIsNumeric = (value: string) => !isNaN(Number(value));

export const beNumeric: StringValidator = {
  predicate: stringIsNumeric,
  message: 'Please enter a number',
};

export const beAnInteger: StringValidator = {
  predicate: (value: string) => stringIsNumeric(value) && Number(value) % 1 === 0,
  message: 'Please enter a whole number',
};

export const beEqualTo = (requiredValue: number): StringValidator => ({
  predicate: (value: string) => stringIsNumeric(value) && Number(value) === requiredValue,
  message: `Must equal ${requiredValue}`,
});

export const bePositiveNumber: StringValidator = {
  predicate: (value: string) => stringIsNumeric(value) && Number(value) > 0,
  message: 'Please enter a positive number',
};

export const beGreaterThan = (threshold: number): StringValidator => ({
  predicate: (value: string) => stringIsNumeric(value) && Number(value) > threshold,
  message: `Please enter a number greater than ${threshold}`,
});

export const beGreaterThanOrEqualTo = (threshold: number): StringValidator => ({
  predicate: (value: string) => stringIsNumeric(value) && Number(value) >= threshold,
  message: `Please enter a number greater than or equal to ${threshold}`,
});

export const beLessThan = (threshold: number): StringValidator => ({
  predicate: (value: string) => stringIsNumeric(value) && Number(value) < threshold,
  message: `Please enter a number less than ${threshold}`,
});

export const beLessThanOrEqualTo = (threshold: number): StringValidator => ({
  predicate: (value: string) => stringIsNumeric(value) && Number(value) <= threshold,
  message: `Please enter a number less than or equal to ${threshold}`,
});

export const beBetweenInclusive = (minimumValue: number, maximumValue: number): StringValidator => ({
  predicate: (value: string) =>
    stringIsNumeric(value) && Number(value) >= minimumValue && Number(value) <= maximumValue,
  message: `Please enter a number between ${minimumValue} and ${maximumValue} (inclusive)`,
});

export const beValidPercentage: StringValidator = {
  predicate: (value: string) => stringIsNumeric(value) && Number(value) >= 0 && Number(value) <= 100,
  message: `Please enter a number between 0 and 100 (inclusive)`,
};

export const haveDecimalPlaces = (allowedDecimalPlaces: number): StringValidator => ({
  predicate: (value: string) =>
    stringIsNumeric(value) && numericStringWithAllowedDecimalPlacesRegex(allowedDecimalPlaces).test(value),
  message: `Please enter a number with at most ${allowedDecimalPlaces} decimal places`,
});

const numericStringWithAllowedDecimalPlacesRegex = (allowedDecimalPlaces: number) =>
  new RegExp(`^(-)?([0-9])*(\\.[0-9]{0,${allowedDecimalPlaces}})?$`);

export const beValidSequence: StringValidator = {
  predicate: (value: string) => numericSequenceRegex.test(value),
  message: 'Please enter a valid sequence in x.y.z format',
};

const numericSequenceRegex = new RegExp('^\\d+\\.\\d+\\.\\d+$');

export const beValidDate: StringValidator = {
  predicate: (value: string) => {
    const date = new Date(value);
    return !isNaN(date.getTime());
  },
  message: 'Please enter a valid date',
};

export const beValidUrl: StringValidator = {
  predicate: (value: string) => {
    try {
      new URL(value);
      return true;
    } catch (error) {
      return false;
    }
  },
  message: 'Please enter a valid URL',
};
